import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApprovalRequest } from '../../types';
import ApprovalQueue from './ApprovalQueue';
import ApprovalDetail from './ApprovalDetail';
import { useApprovalActions } from '../../hooks/useApprovalActions';

interface ApprovalInterfaceProps {
  className?: string;
}

const ApprovalInterface: React.FC<ApprovalInterfaceProps> = ({ className = '' }) => {
  const { id } = useParams<{ id?: string }>();
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRequest | null>(null);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { fetchApprovals } = useApprovalActions();

  // Load approvals on mount
  useEffect(() => {
    const loadApprovals = async () => {
      try {
        setLoading(true);
        const data = await fetchApprovals();
        setApprovals(data);
        
        // If there's an ID in the URL, select that approval
        if (id) {
          const approval = data.find(a => a.id === id);
          if (approval) {
            setSelectedApproval(approval);
          }
        } else if (data.length > 0) {
          // Select first approval by default
          setSelectedApproval(data[0]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load approvals');
      } finally {
        setLoading(false);
      }
    };

    loadApprovals();
  }, [id, fetchApprovals]);

  const handleApprovalSelect = (approval: ApprovalRequest) => {
    setSelectedApproval(approval);
  };

  const handleApprovalAction = async (approvalId: string, action: 'approve' | 'reject', comment?: string) => {
    try {
      // Update the approval status optimistically
      setApprovals(prev => prev.filter(a => a.id !== approvalId));
      
      // If the current approval was acted upon, select the next one
      if (selectedApproval?.id === approvalId) {
        const remainingApprovals = approvals.filter(a => a.id !== approvalId);
        setSelectedApproval(remainingApprovals.length > 0 ? remainingApprovals[0] : null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process approval');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading approvals...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="text-red-800">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Left Panel - Approval Queue */}
      <div className="w-96 border-r bg-gray-50 flex flex-col">
        <div className="p-4 border-b bg-white">
          <h2 className="text-lg font-semibold text-gray-900">
            Pending Approvals ({approvals.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Select an approval to review details
          </p>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <ApprovalQueue
            approvals={approvals}
            selectedApproval={selectedApproval}
            onApprovalSelect={handleApprovalSelect}
            className="h-full"
          />
        </div>
      </div>

      {/* Right Panel - Approval Detail */}
      <div className="flex-1 flex flex-col">
        {selectedApproval ? (
          <ApprovalDetail
            approval={selectedApproval}
            onApprovalAction={handleApprovalAction}
            className="h-full"
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Approval Selected</h3>
              <p className="text-gray-600">
                {approvals.length > 0 
                  ? 'Select an approval from the queue to review details'
                  : 'No pending approvals at this time'
                }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalInterface;
